import { Dispatch, type FC, useRef } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { TemplateSettings } from 'modules/map/components/daily-dish/types';
import { FIELD_WIDTHS } from 'modules/map/components/daily-dish/constants';
import { getBackgroundThumbPath } from 'modules/map/components/daily-dish/config';
import StyledLabel from 'modules/map/components/daily-dish/components/utils/StyledLabel';
import { SelectField, FormFieldRow } from 'modules/map/components/daily-dish/components/form';
import { SettingsAction } from 'modules/map/components/daily-dish/reducer';
import { themeLockOptions } from 'modules/map/components/daily-dish/look/pickWeeklyLook';

interface BackgroundSelectorProps {
  settings: TemplateSettings;
  dispatch: Dispatch<SettingsAction>;
  lockedTheme: string | null;
  previewImages: string[];
  schoolYear?: number | null;
  onLockTheme: (themeId: string | null) => void;
  onUpload: (file: File) => void;
}

const BackgroundSelector: FC<BackgroundSelectorProps> = (props) => {
  const { settings, dispatch, lockedTheme, previewImages, schoolYear, onLockTheme, onUpload } =
    props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const themeOptions = themeLockOptions(schoolYear);

  return (
    <>
      <FormFieldRow gap={1}>
        <StyledLabel label="Chủ đề:" sx={{ width: FIELD_WIDTHS.HALF, marginBottom: 0 }} />
        <SelectField
          label=""
          value={lockedTheme ?? 'week'}
          options={themeOptions}
          onChange={(value) => {
            const next = value.toString();
            onLockTheme(next === 'week' ? null : next);
          }}
          width={FIELD_WIDTHS.HALF}
        />
      </FormFieldRow>
      <Box sx={{ mb: 1 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) {
              onUpload(file);
            }
          }}
        />
        <Button variant="outlined" size="small" onClick={() => fileInputRef.current?.click()}>
          Tải ảnh nền
        </Button>
      </Box>
      <Box
        sx={{
          height: 400,
          my: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          overflowY: 'auto',
          boxShadow: '2px 2px 10px #00000050',
          p: 1,
          scrollbarWidth: 'none',
          alignContent: 'flex-start',
        }}
      >
        {previewImages.length === 0 ? (
          <Box sx={{ width: '100%', py: 6, textAlign: 'center', color: '#6b5e57', fontSize: 14 }}>
            Chưa có ảnh cho chủ đề này
          </Box>
        ) : null}
        {previewImages.map((file) => {
          const isSelected =
            !settings.general.localBackground && settings.general.backgroundFile === file;
          return (
            <Box
              key={file}
              sx={{
                width: FIELD_WIDTHS.HALF,
                flexShrink: 0,
                flexGrow: 0,
                aspectRatio: '16 / 9',
                // Xếp hai lớp: ảnh thu nhỏ nằm trên, ảnh đầy đủ nằm dưới làm lớp đỡ. Nếu ai
                // thêm ảnh nền mà quên tạo ảnh thu nhỏ thì lớp trên không tải được, trình duyệt
                // hiện lớp dưới — ô xem trước vẫn đúng, chỉ nặng hơn, thay vì trống trơn. Lớp dưới
                // KHÔNG được tải khi lớp trên tải xong, nên bình thường không tốn gì.
                backgroundImage: `url("${getBackgroundThumbPath(file)}"), url("/menu-background_image/${file}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: isSelected ? '3px solid #FFB22C' : '3px solid transparent',
                boxShadow: isSelected ? '0 0 10px #FFB22C' : 'none',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
              }}
              onClick={() => dispatch({ type: 'UPDATE_BACKGROUND_FILE', file })}
            />
          );
        })}
      </Box>
    </>
  );
};

export default BackgroundSelector;
